import React, { useState, useEffect, useMemo } from 'react';
import { MaterialReactTable } from 'material-react-table';
import {
  Box,
  Button,
  Avatar,
  CircularProgress,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Session, WorkshopDayScheduleRow } from './sessionTypes';
import {
  formatSessionEndLabel,
  formatSessionStartLabel,
  formatWorkshopDayStartLabel,
  getSessionDeliveryTypeLabel,
  sessionNameSubtitle,
  isOptionalSession,
  isPreviousWorkshopDayCompleted,
  isWorkshopSession,
  resolveWorkshopDays,
  sessionTablePaperSx,
  sessionTableContainerSx,
} from './sessionUtils';
import { LiveKitPodStatusDot } from './LiveKitPodStatusDot';
import { SessionIdBadge } from './SessionIdBadge';
import {
  EARLY_START_BUFFER_MS,
  useSessionStartGate,
  type SessionStartSlot,
} from '../../../../utils/serverTimeIst';
import ConfirmationModal from '../../../../components/ConfirmationModal';

interface UpcomingSessionsProps {
  sessions: Session[];
  batchLabel?: string;
  onDeleteSession: (session: Session) => void;
  onStartSession?: (session: Session, dayIndex?: number) => void;
  onShowReport?: (session: Session) => void;
  onManageStudents?: (session: Session) => void;
  loadingStarts?: Set<number>;
  onSessionsChange?: (sessions: Session[]) => void;
  pagination: { pageIndex: number; pageSize: number };
  rowCount: number;
  onPaginationChange: (pagination: { pageIndex: number; pageSize: number }) => void;
}

function workshopDays(session: Session): WorkshopDayScheduleRow[] {
  return resolveWorkshopDays(session);
}

function topicsForDay(session: Session, day: WorkshopDayScheduleRow): string[] {
  if (Array.isArray(day.topics) && day.topics.length) return day.topics;
  const plan = session.topic_plan || [];
  return plan
    .filter((t) => {
      const idx = t.workshop_day_index ?? t.workshopDayIndex;
      return Number(idx ?? 0) === Number(day.day_index);
    })
    .map((t) => String(t.title || t.name || '').trim())
    .filter(Boolean);
}

const UpcomingSessions: React.FC<UpcomingSessionsProps> = ({
  sessions,
  batchLabel = '',
  onDeleteSession,
  onStartSession,
  onShowReport,
  onManageStudents,
  loadingStarts,
  onSessionsChange,
  pagination,
  rowCount,
  onPaginationChange,
}) => {
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [cancelTarget, setCancelTarget] = useState<Session | null>(null);

  const startSlots = useMemo((): SessionStartSlot[] => {
    const slots: SessionStartSlot[] = [];
    for (const session of sessions) {
      const days = workshopDays(session);
      if (isWorkshopSession(session) && days.length > 0) {
        for (const day of days) {
          slots.push({
            date: day.start_date || session.Date,
            time: day.start_time || session.Start_Time || '',
          });
        }
      } else {
        slots.push({
          date: session.Date,
          time: session.Start_Time || '',
        });
      }
    }
    return slots;
  }, [sessions]);

  const { clockReady, gateVersion, canStartSessionAt } = useSessionStartGate(
    startSlots,
    EARLY_START_BUFFER_MS,
  );

  useEffect(() => {
    if (!onSessionsChange) return;
    const selectedSessions = sessions.filter((s) => rowSelection[s.id.toString()]);
    onSessionsChange(selectedSessions);
  }, [rowSelection, sessions, onSessionsChange]);

  const canStartSession = (session: Session, day?: WorkshopDayScheduleRow): boolean => {
    // gateVersion: re-check after scheduled unlock / clock re-sync
    void gateVersion;
    if (!onStartSession || !clockReady) return false;
    try {
      if (day) {
        const days = workshopDays(session);
        if (!isPreviousWorkshopDayCompleted(days, Number(day.day_index))) return false;
        if (day.can_start === false) return false;
      }
      return canStartSessionAt(
        day?.start_date || session.Date,
        day?.start_time || session.Start_Time || '',
      );
    } catch {
      return false;
    }
  };

  const columns = [
    {
      accessorKey: 'Session_Topic',
      header: 'SESSION NAME',
      Cell: ({ row }: { row: { original: Session; getIsExpanded: () => boolean } }) => {
        const session = row.original;
        const subtitle = sessionNameSubtitle(session, batchLabel);
        const days = workshopDays(session);
        const isWorkshop = isWorkshopSession(session) && days.length > 0;
        return (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
            {isWorkshop ? (
              <Box sx={{ color: '#64748b', mt: 0.25 }}>
                {row.getIsExpanded() ? (
                  <KeyboardArrowDownIcon fontSize="small" />
                ) : (
                  <KeyboardArrowRightIcon fontSize="small" />
                )}
              </Box>
            ) : null}
            <Box>
              <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: 14, lineHeight: 1.3 }}>
                {session.Session_Topic}
              </Typography>
              <SessionIdBadge sessionId={session.id} />
              {subtitle ? (
                <Typography sx={{ color: '#64748b', fontSize: 12, mt: 0.25 }}>
                  {subtitle}
                </Typography>
              ) : null}
              {isWorkshop ? (
                <Typography sx={{ color: '#94a3b8', fontSize: 11, mt: 0.25 }}>
                  {days.length} day{days.length === 1 ? '' : 's'} · click row to expand
                </Typography>
              ) : null}
            </Box>
          </Box>
        );
      },
    },
    {
      accessorKey: 'conductedby',
      header: 'TRAINER',
      Cell: ({ row }: { row: { original: Session } }) => {
        const name = row.original.conductedby || '—';
        const initial = name.trim().charAt(0).toUpperCase() || '?';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ width: 28, height: 28, fontSize: 13, bgcolor: '#e2e8f0', color: '#334155' }}>
              {initial}
            </Avatar>
            <Typography sx={{ fontSize: 14, color: '#0f172a' }}>{name}</Typography>
          </Box>
        );
      },
    },
    {
      id: 'type',
      header: 'TYPE',
      Cell: ({ row }: { row: { original: Session } }) => (
        <Typography sx={{ fontSize: 14, color: '#334155' }}>
          {getSessionDeliveryTypeLabel(row.original)}
        </Typography>
      ),
    },
    {
      id: 'start',
      header: 'START',
      Cell: ({ row }: { row: { original: Session } }) => {
        const session = row.original;
        const days = workshopDays(session);
        if (isWorkshopSession(session) && days.length > 0) {
          return (
            <Typography sx={{ fontSize: 13, color: '#334155' }}>
              {formatWorkshopDayStartLabel(days[0])}
              {days.length > 1 ? (
                <Typography component="span" sx={{ display: 'block', color: '#94a3b8', fontSize: 12 }}>
                  → {formatWorkshopDayStartLabel(days[days.length - 1])}
                </Typography>
              ) : null}
            </Typography>
          );
        }
        return (
          <Typography sx={{ fontSize: 14, color: '#334155', whiteSpace: 'nowrap' }}>
            {formatSessionStartLabel(session)}
          </Typography>
        );
      },
    },
    {
      id: 'end',
      header: 'END',
      Cell: ({ row }: { row: { original: Session } }) => (
        <Typography sx={{ fontSize: 14, color: '#334155', whiteSpace: 'nowrap' }}>
          {formatSessionEndLabel(row.original)}
        </Typography>
      ),
    },
    {
      id: 'actions',
      header: 'ACTIONS',
      size: 180,
      Cell: ({ row }: { row: { original: Session } }) => {
        const session = row.original;
        const joining = loadingStarts?.has(session.id);
        const days = workshopDays(session);
        const isWorkshop = isWorkshopSession(session) && days.length > 0;

        return (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '14px 36px auto 36px',
              gap: 0.75,
              alignItems: 'center',
              width: 'fit-content',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box
              sx={{
                width: 14,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <LiveKitPodStatusDot session={session} />
            </Box>
            <Box sx={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {isOptionalSession(session) && onManageStudents ? (
                <Tooltip title="Manage students">
                  <IconButton
                    size="small"
                    onClick={() => onManageStudents(session)}
                    sx={{ border: '1px solid #cbd5e1', borderRadius: '8px', width: 32, height: 32 }}
                  >
                    <PersonAddOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Box>
            {isWorkshop ? (
              <Typography sx={{ fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>
                Expand for day actions
              </Typography>
            ) : (
              <Button
                variant="contained"
                size="small"
                startIcon={joining ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
                onClick={() => onStartSession?.(session)}
                disabled={!canStartSession(session) || joining}
                sx={{
                  textTransform: 'none',
                  bgcolor: '#2563eb',
                  fontWeight: 600,
                  borderRadius: '8px',
                  minWidth: 84,
                  '&:hover': { bgcolor: '#1d4ed8' },
                  '&.Mui-disabled': { bgcolor: '#93c5fd', color: '#fff' },
                }}
              >
                Start
              </Button>
            )}
            {!isWorkshop ? (
              <Tooltip title="Cancel session">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => setCancelTarget(session)}
                  sx={{
                    border: '1px solid #fca5a5',
                    borderRadius: '8px',
                    width: 32,
                    height: 32,
                  }}
                >
                  <CancelOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <Box sx={{ width: 32 }} />
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', width: '100%' }}>
      <MaterialReactTable
        columns={columns}
        data={sessions}
        enableColumnFilters={false}
        enableRowSelection={false}
        enableExpanding
        getRowCanExpand={(row) => {
          const s = row.original;
          return isWorkshopSession(s) && workshopDays(s).length > 0;
        }}
        displayColumnDefOptions={{
          'mrt-row-expand': {
            size: 0,
            minSize: 0,
            maxSize: 0,
            header: '',
            Cell: () => null,
          },
        }}
        state={{ pagination, expanded }}
        onExpandedChange={(updater: any) => {
          setExpanded((prev) => {
            const next = typeof updater === 'function' ? updater(prev) : updater;
            if (typeof next === 'boolean') return next ? prev : {};
            return (next || {}) as Record<string, boolean>;
          });
        }}
        manualPagination
        rowCount={rowCount}
        onPaginationChange={(updater: any) => {
          const next = typeof updater === 'function' ? updater(pagination) : updater;
          onPaginationChange(next);
        }}
        getRowId={(row) => row.id.toString()}
        renderDetailPanel={({ row }) => {
          const session = row.original;
          const days = workshopDays(session);
          if (!days.length) return null;
          const joining = loadingStarts?.has(session.id);
          return (
            <Box sx={{ bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0', px: 2, py: 1.5 }}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: '#94a3b8',
                  letterSpacing: '0.04em',
                  mb: 1,
                  textTransform: 'uppercase',
                }}
              >
                Workshop days
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {days.map((day) => {
                  const topics = topicsForDay(session, day);
                  const label = day.day_label || `Day ${day.day_index + 1}`;
                  const canStart = canStartSession(session, day);
                  const prevCompleted = isPreviousWorkshopDayCompleted(
                    days,
                    Number(day.day_index),
                  );
                  return (
                    <Box
                      key={day.day_index}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns:
                          'minmax(120px, 1.2fr) minmax(160px, 1fr) minmax(180px, 1.6fr) auto',
                        gap: 2,
                        alignItems: 'center',
                        bgcolor: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        px: 1.5,
                        py: 1.25,
                      }}
                    >
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{label}</Typography>
                        <Typography sx={{ fontSize: 12, color: '#64748b', textTransform: 'capitalize' }}>
                          {day.status || 'scheduled'}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: 13, color: '#334155' }}>
                        {formatWorkshopDayStartLabel(day)}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: '#64748b' }}>
                        {topics.length ? topics.join(' · ') : 'Topics planned for this day'}
                      </Typography>
                      <Box sx={{ justifySelf: 'end', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        {day.has_ended || day.status === 'ended' ? (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onShowReport?.(session);
                            }}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 700,
                              color: '#2563eb',
                              borderColor: '#bfdbfe',
                              '&:hover': { backgroundColor: '#eff6ff', borderColor: '#93c5fd' },
                            }}
                          >
                            Report
                          </Button>
                        ) : (
                          <Tooltip
                            title={
                              prevCompleted
                                ? canStart
                                  ? 'Start this day'
                                  : 'Available at the scheduled start time'
                                : 'Complete the previous day first'
                            }
                          >
                            <span>
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={
                                  joining ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />
                                }
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onStartSession?.(session, day.day_index);
                                }}
                                disabled={!canStart || joining || day.status === 'live'}
                                sx={{
                                  textTransform: 'none',
                                  bgcolor: '#2563eb',
                                  fontWeight: 600,
                                  borderRadius: '8px',
                                  minWidth: 96,
                                  '&:hover': { bgcolor: '#1d4ed8' },
                                  '&.Mui-disabled': { bgcolor: '#93c5fd', color: '#fff' },
                                }}
                              >
                                Start
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                        <Tooltip title="Cancel workshop session">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              setCancelTarget(session);
                            }}
                            sx={{
                              border: '1px solid #fca5a5',
                              borderRadius: '8px',
                              width: 32,
                              height: 32,
                            }}
                          >
                            <CancelOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        }}
        muiTableBodyRowProps={({ row, isDetailPanel }) => {
          if (isDetailPanel) return {};
          const canExpand = row.getCanExpand();
          return {
            onClick: () => {
              if (canExpand) row.toggleExpanded();
            },
            sx: {
              cursor: canExpand ? 'pointer' : 'default',
              '&:hover': canExpand ? { bgcolor: '#f8fafc' } : undefined,
            },
          };
        }}
        muiTablePaperProps={{ sx: { ...sessionTablePaperSx } }}
        enableTopToolbar={false}
        muiTableHeadCellProps={{
          sx: {
            color: '#94a3b8',
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: '0.04em',
            backgroundColor: '#fff',
            borderBottom: '1px solid #e5e7eb',
          },
        }}
        muiTableBodyCellProps={{
          sx: { borderBottom: '1px solid #f1f5f9', py: 1.5 },
        }}
        muiTableContainerProps={{ sx: { ...sessionTableContainerSx } }}
        initialState={{ density: 'comfortable' }}
      />

      <ConfirmationModal
        show={cancelTarget != null}
        onHide={() => setCancelTarget(null)}
        onConfirm={() => {
          if (cancelTarget) onDeleteSession(cancelTarget);
          setCancelTarget(null);
        }}
        title="Cancel session"
        message={
          cancelTarget
            ? `Cancel "${cancelTarget.Session_Topic}"? It will be removed from scheduled sessions.`
            : ''
        }
        confirmText="Cancel session"
        cancelText="Keep session"
        variant="danger"
      />
    </Box>
  );
};

export default UpcomingSessions;
